// hooks/useShifts.ts
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { shiftService } from '@/api/services/shiftService'
import { createApiRetryHandler } from '@/utils/apiRetry'
import type {
  ShiftDispatchersResponse,
  ShiftItem,
  ShiftListParams,
  ShiftListResponse,
  ShiftSwapRequestPayload,
  ShiftTrail,
} from '@/types/api/shifts'
import type {
  ShiftSwapListResponse,
  SwapActionResponse,
  SwapActionPayload,
} from '@/types/api/shifts'

/**
 * Query key factory for shifts
 */
export const SHIFT_QUERY_KEYS = {
  all: ['shifts'] as const,
  lists: () => [...SHIFT_QUERY_KEYS.all, 'list'] as const,
  list: (params: ShiftListParams) =>
    [...SHIFT_QUERY_KEYS.lists(), params] as const,
  details: () => [...SHIFT_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string, shiftTrail?: ShiftTrail) =>
    [...SHIFT_QUERY_KEYS.details(), id, shiftTrail ?? 'default'] as const,
}

/**
 * Hook for fetching shifts list
 */
type ShiftQueryOptions = Omit<
  UseQueryOptions<ShiftListResponse>,
  'queryKey' | 'queryFn'
>

export const useShifts = (
  params: ShiftListParams = {},
  options?: ShiftQueryOptions
) => {
  return useQuery<ShiftListResponse>({
    queryKey: SHIFT_QUERY_KEYS.list(params),
    queryFn: () => shiftService.listShifts(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: createApiRetryHandler(3),
    ...options,
  })
}

/**
 * Hook for fetching shifts by dispatcher ID
 */
export const useShiftsByDispatcher = (
  dispatcherId?: string,
  options?: Pick<
    ShiftListParams,
    'limit' | 'sortBy' | 'sortOrder' | 'from' | 'to'
  >
) => {
  return useShifts({
    dispatcher_id: dispatcherId,
    limit: options?.limit ?? 20,
    sortBy: options?.sortBy ?? 'from',
    sortOrder: options?.sortOrder ?? 'desc',
    from: options?.from,
    to: options?.to,
  })
}

/**
 * Hook for fetching a single shift by ID
 */
type ShiftDetailQueryOptions = Omit<
  UseQueryOptions<ShiftItem | undefined>,
  'queryKey' | 'queryFn'
> & {
  shiftTrail?: ShiftTrail
}

export const useShift = (
  shiftId?: string,
  options?: ShiftDetailQueryOptions
) => {
  const { shiftTrail, ...queryOptions } = options ?? {}

  return useQuery<ShiftItem | undefined>({
    queryKey: SHIFT_QUERY_KEYS.detail(shiftId || '', shiftTrail),
    queryFn: () => shiftService.getShift(shiftId!, shiftTrail),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: createApiRetryHandler(3),
    ...queryOptions,
    enabled: Boolean(shiftId) && (queryOptions.enabled ?? true),
  })
}

/**
 * Hook for searching shifts
 */
export const useSearchShifts = (
  query: string,
  filters?: Partial<ShiftListParams>
) => {
  return useQuery({
    queryKey: ['shifts', 'search', query, filters],
    queryFn: () => shiftService.searchShifts(query, filters),
    enabled: !!query,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: createApiRetryHandler(3),
  })
}

export const useClockInShift = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: {
      shift_id: string
      acknowledged_cases: string[]
      headset_audio_test: boolean
      telephony_logged_in: boolean
      dispatch_console_connected: boolean
      maps_gps_available: boolean
      responder_feed_connected: boolean
      payment_portal_accessible: boolean
      clock_in_consent: boolean
      final_comment?: string
      issues?: Array<{
        system: string
        issueType?: string
        notes?: string
      }>
    }) => {
      return shiftService.clockInShift(payload.shift_id, payload)
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: SHIFT_QUERY_KEYS.all })

      queryClient.setQueryData(
        SHIFT_QUERY_KEYS.detail(variables.shift_id),
        (oldData: any) => {
          if (oldData) {
            return {
              ...oldData,
              clock_in_status: true,
              clock_in_time: new Date().toISOString(),
            }
          }
          return oldData
        }
      )

      console.log('Clock in successful:', data)
    },
    onError: error => {
      console.error('Clock in failed:', error)
    },
  })
}

// In useShifts.ts, add this hook:

/**
 * Hook for clocking out of a shift
 */
export const useClockOutShift = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: {
      shift_id: string
      data: {
        title: string
        total_cases_handled: number
        opened_cases_during_shift: number
        closed_cases_during_shift: number
        pending_cases: number
        major_incident: string
        interventions: Array<{
          case_id: string
          intervention_type: string
          details: string
          time: string
        }>
      }
      active_cases: Array<{
        case_id: string
        disposition: string
        note: string
        status: string
      }>
      final_comment: string
      clock_out_consent: boolean
      message: string
      status: string
    }) => {
      return shiftService.clockOutShift(payload)
    },
    onSuccess: (data, variables) => {
      // Invalidate all shift queries
      queryClient.invalidateQueries({ queryKey: SHIFT_QUERY_KEYS.all })

      // Update the specific shift in cache
      queryClient.setQueryData(
        SHIFT_QUERY_KEYS.detail(variables.shift_id),
        (oldData: any) => {
          if (oldData) {
            return {
              ...oldData,
              clock_out_status: true,
              clock_out_time: new Date().toISOString(),
              status: 'completed',
            }
          }
          return oldData
        }
      )

      console.log('Clock out successful:', data)
    },
    onError: error => {
      console.error('Clock out failed:', error)
      throw error
    },
  })
}

export const useShiftSwapRequests = (params: {
  limit?: number
  lastKey?: string | null
}) => {
  return useQuery<ShiftSwapListResponse>({
    queryKey: ['swap-requests', params],
    queryFn: () => shiftService.listShiftSwapRequests(params),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: createApiRetryHandler(3),
  })
}

export const useProcessShiftSwap = () => {
  const queryClient = useQueryClient()

  return useMutation<SwapActionResponse, Error, SwapActionPayload>({
    mutationFn: payload => shiftService.processShiftSwap(payload),
    onSuccess: () => {
      // Invalidate swap requests query
      queryClient.invalidateQueries({ queryKey: ['swap-requests'] })
    },
  })
}

export const useShiftDispatchers = (shiftId: string) => {
  return useQuery<ShiftDispatchersResponse>({
    queryKey: ['shift-dispatchers', shiftId],
    queryFn: () => shiftService.getShiftDispatchers(shiftId),
    enabled: !!shiftId,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: createApiRetryHandler(3),
  })
}

export const useRequestShiftSwap = () => {
  return useMutation({
    mutationFn: (payload: ShiftSwapRequestPayload) =>
      shiftService.requestShiftSwap(payload),
  })
}
